import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Target, Trophy, TrendingUp, ArrowRight, 
  CheckCircle2, ArrowLeft, Calendar, PiggyBank, ShieldCheck,
  Zap, Bell
} from 'lucide-react';
import Footer from '@/components/Footer';

const ObjetivosInfo = () => {
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-stone-50/90 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-2.5 hover:opacity-75 transition-opacity duration-150 flex-shrink-0">
              <img
                src="/icon-192x192.png"
                alt="PGastos"
                className="h-7 sm:h-8 w-auto"
              />
              <span className="text-base sm:text-lg font-bold text-stone-900 tracking-tight">PGastos</span>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/">
                <Button variant="outline" size="sm" className="border-stone-300 hover:bg-stone-100 text-stone-700 text-xs sm:text-sm active:scale-95 transition-all duration-150 ease-spring cursor-pointer">
                  <ArrowLeft className="w-4 h-4 mr-1.5" />
                  Volver
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="sm" className="bg-stone-900 hover:bg-stone-800 text-white font-semibold text-xs sm:text-sm px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-xl active:scale-95 transition-all duration-150 ease-spring cursor-pointer shadow-sm">
                  Ver Planes
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-stone-200/70 border border-stone-300 text-stone-800 px-3.5 py-1.5 rounded-full mb-6 text-xs sm:text-sm font-semibold shadow-xs">
              <Target className="w-4 h-4 text-indigo-600" />
              <span>Metas y Planificación</span>
            </div>
            
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-stone-900 mb-6 leading-tight tracking-tight">
              Convierte tus proyectos en{' '}
              <span className="text-indigo-600">metas de ahorro reales</span>
            </h1>

            <p className="text-base sm:text-lg text-stone-600 mb-8 leading-relaxed max-w-2xl mx-auto font-medium">
              Fondo de emergencia, vacaciones, el enganche de tu auto o renovar equipo. Define tu objetivo, establece tu fecha límite y la plataforma calculará tu plan de ahorro paso a paso.
            </p>

            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Link to="/?register=true">
                <Button size="lg" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm sm:text-base px-8 py-6 rounded-2xl shadow-md active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Crear Mi Primer Objetivo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border border-stone-300 bg-white hover:bg-stone-100 text-stone-800 font-bold text-sm sm:text-base px-8 py-6 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Ver Planes y Precios
                </Button>
              </Link>
            </div>
          </div>

          {/* Demonstration Card */}
          <div className="max-w-4xl mx-auto mb-20">
            <Card className="p-6 sm:p-10 bg-white border border-stone-200 shadow-xl rounded-3xl">
              <div className="text-center mb-8">
                <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full">
                  Seguimiento en Vivo
                </span>
                <h2 className="text-xl sm:text-2xl font-black text-stone-900 mt-3 tracking-tight">
                  Claridad absoluta en cada objetivo
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6 items-stretch">
                {/* Meta 1: Fondo de Emergencia */}
                <div className="p-6 bg-stone-50 border border-stone-200 rounded-2xl flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center shadow-xs">
                          <PiggyBank className="w-5 h-5 text-indigo-400" />
                        </div>
                        <div>
                          <h3 className="font-bold text-stone-900 text-base">Fondo de Emergencia</h3>
                          <p className="text-xs text-stone-500 font-medium">Meta: 3 meses de gastos</p>
                        </div>
                      </div>
                      <span className="text-xs font-black text-indigo-900 bg-indigo-100 px-2 py-0.5 rounded-full">65%</span>
                    </div>

                    <div className="my-3">
                      <div className="flex justify-between text-xs font-semibold text-stone-700 mb-1">
                        <span>$32,500 MXN ahorrados</span>
                        <span className="text-stone-500 font-normal">Objetivo: $50,000 MXN</span>
                      </div>
                      <div className="w-full bg-stone-200 h-2.5 rounded-full overflow-hidden">
                        <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: '65%' }}></div>
                      </div>
                    </div>
                  </div>

                  <p className="text-[11px] text-stone-600 mt-3 pt-3 border-t border-stone-200 flex items-center justify-between font-medium">
                    <span>💡 Ahorro mensual recomendado:</span>
                    <strong className="text-stone-900 font-bold">$2,916 MXN/mes</strong>
                  </p>
                </div>

                {/* Meta 2: Vacaciones Japón */}
                <div className="p-6 bg-stone-50 border border-stone-200 rounded-2xl flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center shadow-xs">
                          <Trophy className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                          <h3 className="font-bold text-stone-900 text-base">Vacaciones Familiares</h3>
                          <p className="text-xs text-stone-500 font-medium">Fecha límite: Diciembre 2026</p>
                        </div>
                      </div>
                      <span className="text-xs font-black text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">88%</span>
                    </div>

                    <div className="my-3">
                      <div className="flex justify-between text-xs font-semibold text-stone-700 mb-1">
                        <span>$44,000 MXN ahorrados</span>
                        <span className="text-stone-500 font-normal">Objetivo: $50,000 MXN</span>
                      </div>
                      <div className="w-full bg-stone-200 h-2.5 rounded-full overflow-hidden">
                        <div className="bg-emerald-600 h-2.5 rounded-full" style={{ width: '88%' }}></div>
                      </div>
                    </div>
                  </div>

                  <p className="text-[11px] text-stone-600 mt-3 pt-3 border-t border-stone-200 flex items-center justify-between font-medium">
                    <span>🎯 ¡Estás a solo $6,000 de cumplirla!</span>
                    <strong className="text-emerald-700 font-bold">Casi listo ✓</strong>
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-20">
            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <Target className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Metas Sin Límites</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Crea tantas metas como necesites: corto plazo (gadgets), mediano plazo (viajes) o largo plazo (retiro e inversiones).
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <Calendar className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Cálculo de Ahorro Mensual</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Indica tu fecha objetivo y la plataforma calculará automáticamente cuánto dinero debes separar cada quincena o mes.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <TrendingUp className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Progreso Visual Dinámico</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Barras de avance con porcentaje en tiempo real y cálculo de días restantes para mantener alta tu motivación de ahorro.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <Bell className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Recordatorios de Aporte</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Recibe notificaciones en tus fechas de cobro para que deposites a tu meta antes de comprometer tu dinero.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Historial de Aportaciones</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Bitácora completa con fecha y monto de cada abono realizado para que veas el fruto de tu constancia paso a paso.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Seguridad y Respaldo</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Tus metas están sincronizadas en la nube con respaldo automático disponible en tu teléfono, tableta o computadora.
              </p>
            </Card>
          </div>

          {/* CTA Section */}
          <Card className="p-8 sm:p-14 bg-stone-900 text-white text-center rounded-3xl border border-stone-800 shadow-xl">
            <h2 className="text-2xl sm:text-4xl font-black mb-3 tracking-tight">
              Comienza hoy a construir tu patrimonio
            </h2>
            <p className="text-sm sm:text-base text-stone-400 mb-8 max-w-xl mx-auto leading-relaxed font-medium">
              El mejor momento para empezar a ahorrar fue ayer; el segundo mejor momento es hoy.
            </p>
            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Link to="/?register=true">
                <Button size="lg" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white text-sm sm:text-base font-bold px-8 py-6 rounded-2xl shadow-md active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Crear Cuenta Gratis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border border-stone-700 bg-stone-800 hover:bg-stone-700 text-white text-sm sm:text-base font-bold px-8 py-6 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Ver Planes Disponibles
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ObjetivosInfo;
